/**
 * KPIFilterBar.jsx  – stessa logica di prima, cambiano SOLO i pulsanti.
 * - Preset "Ultimi 30 gg" rimosso.
 * - Restyling pulsanti (flat-modern) senza toccare il resto della toolbar.
 */
import React, { useState } from "react";
import {
  addDays, differenceInDays, format, startOfMonth, startOfWeek,
} from "date-fns";
import toast, { Toaster } from "react-hot-toast";

const MAX_RANGE = 30;
const iso = (d) => format(d, "yyyy-MM-dd");
const toMidnight = (s) => new Date(`${s}T00:00`);

export default function KPIFilterBar({
  startDate,
  endDate,
  onChangeStart,
  onChangeEnd,
  onFilter,
  restaurantsList = [],
  selectedRestaurant = "all",
  onChangeRestaurant,
}) {
  const today = new Date();
  const [start, setStart] = useState(
    startDate ? toMidnight(startDate) : addDays(today, -6)
  );
  const [end, setEnd] = useState(endDate ? toMidnight(endDate) : today);

  /* ------------ utils ------------ */
  const validate = (s, e) => {
    if (e < s) {
      toast.error("La data di fine dev’essere successiva alla data di inizio");
      return false;
    }
    if (differenceInDays(e, s) > MAX_RANGE) {
      toast.error(`Intervallo massimo: ${MAX_RANGE} giorni`);
      return false;
    }
    return true;
  };

  const applyDates = (s, e) => {
    setStart(s);
    setEnd(e);
    onChangeStart?.(iso(s));
    onChangeEnd?.(iso(e));
  };

  /* ------------ handlers ------------ */
  const changeStart = (v) => {
    const s = toMidnight(v);
    setStart(s); onChangeStart?.(v);
    if (differenceInDays(end, s) > MAX_RANGE) {
      const fix = addDays(s, MAX_RANGE);
      const e = fix > today ? today : fix;
      setEnd(e); onChangeEnd?.(iso(e));
    }
  };
  const changeEnd = (v) => { setEnd(toMidnight(v)); onChangeEnd?.(v); };
  const handleFilter = () => { if (validate(start, end)) onFilter?.(); };

  /* ------------ presets ------------ */
  const presetToday  = () => applyDates(today, today);
  const presetWeek   = () => applyDates(startOfWeek(today, { weekStartsOn: 1 }), today);
  const presetMonth  = () => applyDates(startOfMonth(today), today);

  /* ------------ UI ------------ */
  return (
    <>
      <Toaster position="top-center" toastOptions={{
        style: { background:"#FFFAEE", color:"#924000", fontSize:".9rem" },
        iconTheme:{ primary:"#FF4500", secondary:"#FFF7E6" }
      }}/>

      <div style={st.container}>
        {/* range */}
        <div style={st.row}>
          <input type="date" value={iso(start)} onChange={(e)=>changeStart(e.target.value)} style={st.date}/>
          <span style={{margin:"0 .4rem"}}>→</span>
          <input type="date" value={iso(end)} onChange={(e)=>changeEnd(e.target.value)}
                 min={iso(start)} max={iso(addDays(start,MAX_RANGE))} style={st.date}/>
          <button style={st.mainBtn} onClick={handleFilter}>Filtra</button>
        </div>

        {/* preset */}
        <div style={st.presetRow}>
          <button style={st.presetBtn} onClick={presetToday}>Oggi</button>
          <button style={st.presetBtn} onClick={presetWeek}>Settimana corrente</button>
          <button style={st.presetBtn} onClick={presetMonth}>Mese corrente</button>
        </div>

        {/* ristorante */}
        <select value={selectedRestaurant}
                onChange={(e)=>onChangeRestaurant?.(e.target.value)}
                style={st.select}>
          <option value="all">Tutti i ristoranti</option>
          {restaurantsList.map(r=>(
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>
    </>
  );
}

/* ---------- styles (solo pulsanti cambiati) ---------- */
const baseBtn = {
  border: "none",
  cursor: "pointer",
  transition: "background .15s, color .15s, box-shadow .15s",
  fontWeight: 600,
};

const st = {
  container:{
    display:"flex",flexDirection:"column",gap:".8rem",
    padding:"1rem",
    background:"linear-gradient(135deg,#FF7A00 0%,#FF3C00 100%)",
    borderRadius:12,color:"#FFF",
  },
  row:{display:"flex",alignItems:"center",gap:".4rem"},
  date:{
    padding:".5rem .7rem",borderRadius:8,border:"none",
    fontSize:".9rem",
  },
  /* Main filter button: white pill, orange shadow, hover invert */
  mainBtn:{
    ...baseBtn,
    padding:".55rem 1.1rem",
    borderRadius:24,
    background:"#FFFFFF",
    color:"#FF4500",
    boxShadow:"0 2px 4px rgba(0,0,0,0.15)",
  },
  presetRow:{display:"flex",gap:".5rem",flexWrap:"wrap"},
  /* Preset buttons: transparent with white border, subtle hover */
  presetBtn:{
    ...baseBtn,
    padding:".35rem .9rem",
    borderRadius:24,
    background:"transparent",
    color:"#FFFFFF",
    border:"1px solid rgba(255,255,255,0.6)",
  },
  select:{
    padding:".55rem",
    borderRadius:8,border:"none",
    fontSize:".9rem",
    color:"#333",
  },
};

/* hover/active pseudo via DOM style injection */
const addHover = (selector, cssObj) => {
  const style = document.createElement("style");
  const rules = Object.entries(cssObj)
    .map(([k,v])=>`${k}:${v}`)
    .join(";");
  style.innerHTML = `${selector}:hover{${rules}}`;
  document.head.appendChild(style);
};
addHover("button[data-main]",   {background:"#FF4500",color:"#FFF"});
addHover("button[data-preset]", {background:"rgba(255,255,255,0.15)"});
/* tag buttons for pseudo */
st.mainBtn["data-main"] = "";
st.presetBtn["data-preset"] = "";
